import React, { useState, forwardRef, useImperativeHandle } from 'react';
import api from '../../../services/api';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { addPagination } from '../../Shared/ReportGeneratorLandscape';
import styled from 'styled-components';

const StyledReportFilters = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StyledFilterGroup = styled.div`
  min-width: 200px;

  fieldset {
    border: 1px solid #ddd;
    border-radius: 6px;
    padding: 10px;
  }

  legend {
    font-weight: bold;
    color: var(--text-color);
    padding: 0 5px;
  }

  input {
    width: 95%;
    padding: 8px;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    font-size: 14px;
  }

  input:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 5px rgba(0, 123, 255, 0.5);
  }
`;

const StyledStudentList = styled.ul`
  position: absolute;
  width: 12%;
  list-style-type: none;
  padding: 0;
  margin: 0;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background-color: var(--background-color);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow-x: hidden;
  z-index: 199;

  li {
    width: 100%;
    padding: 10px;
    cursor: pointer;
    transition: background-color 0.3s ease;

    &:hover {
      background-color: var(--primary-color);
      color: var(--header-color);
    }

    &:not(:last-child) {
      border-bottom: 1px solid var(--border-color);
    }
  }
`;

const AttendanceReportByStudent = forwardRef(({ onGenerateReport, userName }, ref) => {
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        student: '',
        studentNameFilter: ''
    });
    const [filteredStudents, setFilteredStudents] = useState([]);

    // Função para formatar data para padrão brasileiro (dd/mm/aaaa)
    const formatDateToBR = (dateString) => {
        if (!dateString) return '-';
        // Divide a string de data para evitar problemas de fuso horário
        const [year, month, day] = dateString.split('-');
        const date = new Date(year, month - 1, day); // month - 1 porque o Date usa índice 0-11 para meses
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const handleClearFilters = () => {
        setFilters({
            student: '',
            studentNameFilter: ''
        });
        setFilteredStudents([]);
    };

    const handleStudentNameFilterChange = async (e) => {
        const studentNameFilter = e.target.value;
        setFilters({ ...filters, studentNameFilter });
        if (studentNameFilter.length >= 3) {
            try {
                const response = await api.get(`/students/index?full_name=${studentNameFilter}`);
                setFilteredStudents(response.data.data);
            } catch (error) {
                console.error("Erro ao buscar estudantes:", error);
                setFilteredStudents([]);
            }
        } else {
            setFilteredStudents([]);
        }
    };

    const handleStudentSelect = (student) => {
        setFilters({ ...filters, student: student.id, studentNameFilter: `${student.first_name} ${student.last_name}` });
        setFilteredStudents([]);
    };

    const getReportData = async () => {
        if (!filters.student) {
            alert("Por favor, selecione um aluno antes de gerar o relatório.");
            return [];
        }

        try {
            setLoading(true);
            const params = {
                student: filters.student
            };
            const response = await api.get('/reports/attendance-report-by-student', { params });
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar relatório de chamada:', error);
            return [];
        } finally {
            setLoading(false);
        }
    };

    const generateTable = async (doc, reportData, userName, filters, recordCount, reportTitle) => {
        let startY = 50;

        reportData.forEach((student) => {
            doc.setFontSize(14);
            doc.setTextColor(40);
            doc.text(`Aluno: ${student.student_name}`, 14, startY);
            startY += 10;

            student.courses.forEach((course) => {
                doc.setFontSize(12);
                doc.setTextColor(40);
                doc.text(`Curso: ${course.category_name} | Professor: ${course.professor_name} | Turma: ${course.course_name}`, 14, startY);
                startY += 5;

                // Ordenar as sessões por data
                const sortedSessions = course.sessions.sort((a, b) => {
                    const dateA = new Date(a.date);
                    const dateB = new Date(b.date);
                    return dateA - dateB;
                });

                const headers = ['Data', 'Horário', 'Presença', 'Comentários'];
                const body = sortedSessions.map((session) => [
                    formatDateToBR(session.date),
                    `${session.start_time} - ${session.end_time}`,
                    session.attendance_status === 'P' ? 'P' : (session.attendance_status === null ? '-' : 'F'),
                    session.comments || '-'
                ]);

                doc.autoTable({
                    head: [headers],
                    body: body,
                    startY: startY,
                    margin: { top: 40 },
                    styles: { fontSize: 8, cellPadding: 0.5, valign: 'middle' },
                    headStyles: {
                        fillColor: [48, 65, 98],
                        lineWidth: 0.1,
                        lineColor: [0, 0, 0],
                        halign: 'center',
                    },
                    columnStyles: {
                        0: { halign: 'center', lineWidth: 0.1, lineColor: [0, 0, 0] },
                        1: { halign: 'center', lineWidth: 0.1, lineColor: [0, 0, 0] },
                        2: { halign: 'center', lineWidth: 0.1, lineColor: [0, 0, 0] },
                        3: { halign: 'left', lineWidth: 0.1, lineColor: [0, 0, 0] },
                    },
                    didDrawCell: function (data) {
                        const doc = data.doc;
                        const cell = data.cell;
                        doc.setLineWidth(0.1);
                        doc.setDrawColor(0, 0, 0);
                        doc.line(cell.x, cell.y, cell.x, cell.y + cell.height);
                        doc.line(cell.x + cell.width, cell.y, cell.x + cell.width, cell.y + cell.height);
                    }
                });

                startY = doc.autoTable.previous.finalY + 15;
            });

            startY += 10;
        });
    };

    const generateReport = async () => {
        // Validação obrigatória do aluno
        if (!filters.student) {
            alert("Por favor, selecione um aluno antes de gerar o relatório.");
            return;
        }

        try {
            const doc = new jsPDF({ format: 'a4', orientation: 'portrait' });
            const reportData = await getReportData();
            
            if (!reportData || reportData.length === 0) {
                alert("Nenhum dado encontrado para o relatório.");
                return;
            }

            const currentUserName = userName || "Nome do Usuário";
            const filtersString = getFilters();
            // Calcular o total de registros (sessões de chamada) de todos os cursos
            const recordCount = reportData.reduce((total, student) => {
                return total + student.courses.reduce((courseTotal, course) => {
                    return courseTotal + course.sessions.length;
                }, 0);
            }, 0);
            const totalPagesExp = "{total_pages_count_string}";

            await generateTable(doc, reportData, currentUserName, filtersString, recordCount, 'Relatório de Chamada por Aluno');
            addPagination(doc, currentUserName, filtersString, recordCount, 'Relatório de Chamada por Aluno', totalPagesExp);

            if (typeof doc.putTotalPages === 'function') {
                doc.putTotalPages(totalPagesExp);
            }

            const pdfBytes = doc.output('arraybuffer');
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            onGenerateReport(url);
        } catch (error) {
            console.error("Erro ao gerar relatório:", error);
            alert("Erro ao gerar relatório. Por favor, tente novamente mais tarde.");
        }
    };

    const getFilters = () => {
        const filtersArray = [];
        if (filters.student) filtersArray.push(`Aluno: ${filters.studentNameFilter}`);
        return filtersArray.join(', ');
    };

    useImperativeHandle(ref, () => ({
        generateReport,
        clearFilters: handleClearFilters,
    }));

    if (loading) {
        return <div>Carregando...</div>;
    }

    return (
        <StyledReportFilters>
            <StyledFilterGroup>
                <fieldset>
                    <legend>Aluno: *</legend>
                    <input
                        type="text"
                        placeholder="Nome do Aluno (obrigatório)"
                        value={filters.studentNameFilter}
                        onChange={handleStudentNameFilterChange}
                        style={{
                            borderColor: !filters.student ? '#ff6b6b' : '#ddd',
                            backgroundColor: !filters.student ? '#fff5f5' : '#fff'
                        }}
                    />
                    {filteredStudents.length > 0 && (
                        <StyledStudentList>
                            {filteredStudents.map((student) => (
                                <li key={student.id} onClick={() => handleStudentSelect(student)}>
                                    {`${student.first_name} ${student.last_name}`}
                                </li>
                            ))}
                        </StyledStudentList>
                    )}
                    {!filters.student && (
                        <div style={{ color: '#ff6b6b', fontSize: '12px', marginTop: '5px' }}>
                            * Campo obrigatório
                        </div>
                    )}
                </fieldset>
            </StyledFilterGroup>
        </StyledReportFilters>
    );
});

export default AttendanceReportByStudent;
